import React, { createContext, FC, RefObject, useContext, useEffect } from 'react';
import cx from 'classnames';
import { DropdownState, useDropdown } from "../hooks/useDropdown";
import { getUnrenderedElementBounds } from "../utils";
import './Dropdown.css';

interface DropdownProps {
  className?: string;
  button: JSX.Element|JSX.Element[]|string;
  disabled?: boolean;
  // If provided, the dropdown will be positioned relative to the parent element. This is necessary in the case that the parent element is scrollable ([more info](https://stackoverflow.com/questions/32616525/allow-a-drop-down-element-to-overflow-a-container-that-has-hidden-or-scrolled-ov))
  parentRef?: RefObject<HTMLElement> | undefined;
  children: any;
}

export const DropdownContext = createContext({} as DropdownState);

/**
 * A generic dropdown component
 * @param className
 * @param button
 * @param disabled
 * @param parentRef - If provided, the dropdown will be positioned relative to the parent element. This is necessary in the case that the parent element is scrollable ([more info](https://stackoverflow.com/questions/32616525/allow-a-drop-down-element-to-overflow-a-container-that-has-hidden-or-scrolled-ov))
 * @param children
 * @constructor
 */
export const Dropdown: FC<DropdownProps> = ({ className, button, disabled, parentRef, children }) => {
  const [offsetTop, setOffsetTop] = React.useState(0);
  const dropdownState = useDropdown();
  const { isDropdownOpen, setIsDropdownOpen, dropdownContainerRef } = dropdownState;
  const dropdownContentRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    // If the dropdown is inside a scrollable parent, we need to disable scrolling on the parent when the dropdown is open
    // as in this case it will not scroll with the parent.
    if (parentRef?.current === undefined || parentRef?.current === null) {
      return;
    }

    const y = parentRef.current.scrollTop;
    if(y === undefined) {
      return;
    }

    const preventScrolling = () => {
      if(parentRef?.current === undefined || parentRef?.current === null) {
        return;
      }
      parentRef.current.scrollTo({
        top: y,
        behavior: 'auto'
      });
    };

    if(isDropdownOpen) {
      parentRef.current.addEventListener('scroll', preventScrolling);
    }

    return () => {
      if(parentRef?.current === undefined || parentRef?.current === null) {
        return;
      }
      parentRef.current.removeEventListener('scroll', preventScrolling);
    };
  }, [isDropdownOpen]);

  const onClick = () => {
    setOffsetTop(calculateOffsetTop());
    setIsDropdownOpen(!isDropdownOpen);
  };

  const calculateOffsetTop = () => {
    if (!dropdownContentRef?.current || !dropdownContainerRef?.current || !parentRef?.current) return 0;

    const dropdownOffsetTop = dropdownContainerRef.current.offsetTop;
    const parentScrollTop = parentRef.current.scrollTop;
    let offsetTop = dropdownOffsetTop - parentScrollTop;

    const {
      height: dropdownContainerHeight,
      top: dropdownContainerOffsetToTop
    } = dropdownContainerRef.current.getBoundingClientRect();
    const { height: dropdownContentHeight } = getUnrenderedElementBounds(dropdownContentRef.current);
    const dropdownContentAndButtonHeight = dropdownContentHeight + dropdownContainerHeight;

    const isDropdownTooCloseToBottom =
      dropdownContainerOffsetToTop + dropdownContentAndButtonHeight > window.innerHeight;

    if (isDropdownTooCloseToBottom) {
      offsetTop -= dropdownContentAndButtonHeight;
    }

    return offsetTop;
  };

  return (
    <DropdownContext.Provider value={dropdownState}>
      <div
        ref={dropdownContainerRef}
        className={ cx('dropdown-container', className, {
          'position-from-parent': parentRef !== undefined,
          open: isDropdownOpen,
          disabled
        })}>
        <button
          className="dropdown-button"
          onClick={onClick}
          disabled={disabled}
        >
          {button}
        </button>
        <div ref={dropdownContentRef} className="dropdown-content" style={offsetTop ? {top: `${offsetTop}px`} : {}}>
          {children}
        </div>
      </div>
    </DropdownContext.Provider>
  );
};

interface DropdownItemProps {
  children: any;
  onClick?: () => void;
  className?: string | undefined;
  title?: string;
  disabled?: boolean;
  isSelected?: boolean;
  stayOpenAfterClick?: boolean;
}

export const DropdownItem: FC<DropdownItemProps> = ({
  children,
  onClick,
  className,
  title,
  disabled,
  isSelected,
  stayOpenAfterClick,
}) => {
  const { setIsDropdownOpen } = useContext(DropdownContext);

  const onClickHandler = (event) => {
    if (disabled) {
      event.stopPropagation();
      return;
    }

    if (!stayOpenAfterClick) {
      setIsDropdownOpen(false);
    }

    if (onClick !== undefined)
      onClick();
  };

  return (
    <div
      className={cx('dropdown-item', className, {
        disabled,
        selected: isSelected,
      })}
      title={title}
      onClick={onClickHandler}
    >
      { children }
    </div>
  );
};